import {
  Bird, BookOpen, Bot, Box, Briefcase, Bug, Building2, CalendarDays, Camera,
  ChartNoAxesCombined, Cloud, Code2, Compass, Contact, Database, FileText, Flag,
  Flame, Folder, FolderKanban, Gem, GitBranch, Globe, Heart, Image, Layers, Leaf,
  Lightbulb, MapPin, Megaphone, MessageCircle, Monitor, Moon, Mountain, Music,
  Palette, PenTool, Rocket, ShieldCheck, Smartphone, Sparkles, Star, Sun, Target,
  Terminal, Trophy, Users, Video, Workflow, Zap, type LucideIcon,
} from "lucide-react";

export const DEFAULT_PROJECT_COLOR = "#c7ad7d";
export const DEFAULT_PROJECT_ICON = "lucide:folder";
export type ProjectSymbol = { value: string; en: string; ko: string; glyph: LucideIcon };

// Stable IDs are persisted in Project.icon; only these 50 glyphs are bundled.
const icons: [string, LucideIcon, string, string][] = [
  ["folder", Folder, "Folder", "폴더"],
  ["folder-kanban", FolderKanban, "Board", "보드"],
  ["briefcase", Briefcase, "Briefcase work", "업무 가방"],
  ["building", Building2, "Building company", "건물 회사"],
  ["users", Users, "Team people", "팀 사람"],
  ["contact", Contact, "Contact profile", "연락처 프로필"],
  ["target", Target, "Target goal", "목표 타겟"],
  ["flag", Flag, "Flag", "깃발"],
  ["rocket", Rocket, "Rocket launch", "로켓 출시"],
  ["compass", Compass, "Compass direction", "나침반 방향"],
  ["heart", Heart, "Heart love", "하트 사랑"],
  ["star", Star, "Star", "별"],
  ["sparkles", Sparkles, "Sparkles", "반짝임"],
  ["sun", Sun, "Sun", "해 태양"],
  ["moon", Moon, "Moon", "달"],
  ["cloud", Cloud, "Cloud", "구름"],
  ["zap", Zap, "Lightning energy", "번개 에너지"],
  ["flame", Flame, "Flame fire", "불 불꽃"],
  ["leaf", Leaf, "Leaf nature", "잎 자연"],
  ["mountain", Mountain, "Mountain", "산"],
  ["gem", Gem, "Gem diamond", "보석 다이아몬드"],
  ["trophy", Trophy, "Trophy award", "트로피 수상"],
  ["globe", Globe, "Globe world", "지구 세계"],
  ["map-pin", MapPin, "Location pin", "위치 핀"],
  ["code", Code2, "Code development", "코드 개발"],
  ["terminal", Terminal, "Terminal console", "터미널 콘솔"],
  ["database", Database, "Database data", "데이터베이스 데이터"],
  ["monitor", Monitor, "Monitor desktop", "모니터 컴퓨터"],
  ["smartphone", Smartphone, "Phone mobile", "휴대폰 모바일"],
  ["bot", Bot, "Robot AI", "로봇 인공지능"],
  ["bug", Bug, "Bug", "버그"],
  ["shield", ShieldCheck, "Shield security", "방패 보안"],
  ["git-branch", GitBranch, "Git branch", "깃 브랜치"],
  ["box", Box, "Box cube", "상자 큐브"],
  ["layers", Layers, "Layers", "레이어 계층"],
  ["workflow", Workflow, "Workflow", "워크플로 흐름"],
  ["palette", Palette, "Palette design", "팔레트 디자인"],
  ["pen-tool", PenTool, "Pen tool vector", "펜 도구 벡터"],
  ["camera", Camera, "Camera photo", "카메라 사진"],
  ["image", Image, "Image picture", "이미지 그림"],
  ["video", Video, "Video movie", "영상 영화"],
  ["music", Music, "Music", "음악"],
  ["book", BookOpen, "Book reading", "책 독서"],
  ["file", FileText, "Document file", "문서 파일"],
  ["chart", ChartNoAxesCombined, "Chart growth", "차트 성장"],
  ["calendar", CalendarDays, "Calendar schedule", "캘린더 일정"],
  ["lightbulb", Lightbulb, "Lightbulb idea", "전구 아이디어"],
  ["megaphone", Megaphone, "Megaphone marketing", "확성기 마케팅"],
  ["message", MessageCircle, "Message chat", "메시지 대화"],
  ["bird", Bird, "Bird", "새"],
];
export const PROJECT_ICONS: ProjectSymbol[] = icons.map(([id, glyph, en, ko]) => ({ value: `lucide:${id}`, glyph, en, ko }));
const symbols = new Map(PROJECT_ICONS.map(symbol => [symbol.value, symbol]));
export const projectSymbol = (value?: string | null) => value ? symbols.get(value) : undefined;
export const validProjectColor = (value: string) => /^#[0-9a-f]{6}$/i.test(value);
export function projectColorInk(color: string) {
  if (!validProjectColor(color)) return "var(--accent-text)";
  const rgb = [1, 3, 5].map(offset => parseInt(color.slice(offset, offset + 2), 16));
  return rgb[0]! * .299 + rgb[1]! * .587 + rgb[2]! * .114 > 150 ? "#211e18" : "#ffffff";
}
export const PROJECT_COLORS = [
  { value: "#aaaeb9", en: "Silver", ko: "실버" },
  { value: "#8fa2bd", en: "Slate blue", ko: "슬레이트 블루" },
  { value: "#c7ad7d", en: "Champagne gold", ko: "샴페인 골드" },
  { value: "#a29bc7", en: "Lavender", ko: "라벤더" },
  { value: "#7bb7b0", en: "Teal", ko: "틸" },
  { value: "#98b391", en: "Sage", ko: "세이지" },
  { value: "#ca9680", en: "Terracotta", ko: "테라코타" },
  { value: "#c88f9f", en: "Rose", ko: "로즈" },
];
